<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="space-x-3">
                <ButtonSecondaryIconSm @click="quit">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span>{{
                    mode === "create"
                        ? $t("pricing.variants.add_variant")
                        : $t("pricing.variants.edit_variant")
                }}</span>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <FormToggleSm
                    :title="$t('pricing.variants.highlight')"
                    v-model="variant.highlight"
                />

                <FormInput
                    id="variantName"
                    :title="$t('pricing.variants.name_label')"
                    :placeholder="$t('pricing.variants.name_placeholder')"
                    v-model="variant.name"
                />

                <div class="space-y-2">
                    <FormInput
                        v-if="pricing.ecId && ecVariant?.title"
                        disabled
                        :title="$t('shared.title')"
                        v-model="ecVariant.title"
                        :help="$t('pricing.ec_info')"
                    />
                    <FormRichText
                        id="variantTitle"
                        :title="ecVariant?.title ? '' : $t('shared.title')"
                        :placeholder="$t('shared.title')"
                        v-model="variant.title"
                        :rows="2"
                    />
                </div>

                <FormInput
                    v-if="pricing.ecId"
                    disabled
                    :title="$t('pricing.variants.price')"
                    v-model="variant.price"
                />

                <FormInput
                    v-else
                    id="variantPrice"
                    :title="$t('pricing.variants.price')"
                    :placeholder="'129'"
                    v-model="variant.price"
                />

                <FormInput
                    v-if="pricing.ecId"
                    disabled
                    :title="$t('pricing.variants.promo_price')"
                    v-model="variant.promoPrice"
                />
                <FormInput
                    v-else
                    id="variantPromoPrice"
                    :title="$t('pricing.variants.promo_price')"
                    :placeholder="'99'"
                    v-model="variant.promoPrice"
                />

                <div class="space-y-2">
                    <FormInput
                        v-if="ecVariant?.priceInfo"
                        disabled
                        :title="$t('pricing.variants.currency_period')"
                        v-model="ecVariant.priceInfo"
                        :help="$t('pricing.ec_info')"
                    />
                    <FormInput
                        id="variantPriceInfo"
                        v-if="!pricing.ecTenantId"
                        :title="ecVariant?.priceInfo ? '' : $t('pricing.variants.currency_period')"
                        :placeholder="'USD'"
                        v-model="variant.priceInfo"
                    />
                </div>

                <div class="space-y-2">
                    <FormInput
                        v-if="ecVariant?.buyLink"
                        disabled
                        v-model="ecVariant.buyLink"
                        :title="$t('pricing.variants.buy_link')"
                        :help="$t('pricing.ec_info')"
                    />
                    <FormInput
                        :title="ecVariant?.buyLink ? '' : $t('pricing.variants.buy_link')"
                        :placeholder="'Link'"
                        v-model="variant.buyLink"
                    />
                </div>

                <FormInput
                    id="variantBadge"
                    :title="$t('pricing.variants.badge')"
                    :placeholder="$t('pricing.variants.super_offer')"
                    v-model="variant.badge"
                />
                <div class="space-y-2">
                    <FormTextArea
                        v-if="ecVariant?.desc"
                        :title="$t('pricing.variants.description')"
                        disabled
                        v-model="ecVariant.desc"
                        :help="$t('pricing.ec_info')"
                    />
                    <FormRichText
                        id="variantDesc"
                        :title="ecVariant?.desc ? '' : $t('pricing.variants.description')"
                        :placeholder="'...'"
                        v-model="variant.desc"
                        :rows="5"
                    />
                </div>

                <div class="space-y-1">
                    <FormTextArea
                        id="variantPoints"
                        :title="$t('pricing.variants.points')"
                        :placeholder="$t('pricing.variants.positive_feature')"
                        v-model="variant.points"
                        :rows="5"
                    />
                    <p class="ct-text">
                        {{ $t("pricing.variants.new_line") }}
                    </p>
                    <p class="ct-text">
                        {{ $t("pricing.variants.special_character") }}
                    </p>
                    <p class="ct-text flex items-center">
                        <span class="bg-white px-1 font-bold mr-1">+</span>
                        {{ $t("pricing.variants.starts_point") }}
                        <CheckIcon class="ml-1 w-4 h-4 ct-text-success" />
                    </p>
                    <p class="ct-text flex items-center">
                        <span class="bg-white px-1 font-bold mr-1">x</span>
                        {{ $t("pricing.variants.starts_point") }}
                        <XIcon class="ml-1 w-4 h-4 ct-text-error" />
                    </p>
                </div>

                <div class="space-y-2">
                    <FormLabel v-if="ecVariant?.img">
                        {{ $t("pricing.variants.variant_photo") }}
                    </FormLabel>
                    <CardFileHorizontal :file="ecFile" v-if="ecFile.url" />
                    <p class="ct-text" v-if="ecVariant?.img">
                        {{ $t("pricing.ec_info") }}
                    </p>
                    <div id="variantImg">
                        <FormFiles
                            :title="ecVariant?.img ? '' : $t('pricing.variants.variant_photo')"
                            accept="image/*"
                            :labels="{
                                clickToUpload: $t('shared.uploader.click_to_upload'),
                                dragAndDrop: $t('shared.uploader.drag_and_drop'),
                                fileInfo: $t('shared.uploader.file_details'),
                            }"
                            :maxSize="10000"
                            :errorMessages="{
                                invalidSize: $t('shared.invalid.file_size_over'),
                                invalidType: $t('shared.invalid.file_type'),
                            }"
                            :single-file="true"
                            v-model="files"
                            @fileSelected="uploadImg"
                            @fileRemoved="removeImg"
                        />
                    </div>
                </div>
                <FormInput
                    id="variantBtnLabel"
                    :title="$t('pricing.variants.cta_label')"
                    v-model="variant.btnLabel"
                    :help="$t('pricing.variants.empty_info')"
                />
            </div>
        </template>
        <template #sidebarFooter>
            <cSaveButton @save="$emit('save')" :disabled="!isDirty" />
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { ButtonSecondaryIconSm } from "@easy/ui/buttons";
import { ArrowLeftIcon, CheckIcon, XIcon } from "@easy/ui/icons";
import {
    FormInput,
    FormTextArea,
    FormToggleSm,
    FormFiles,
    makeFileModel,
    FormLabel,
    FormRichText,
} from "@easy/ui/forms";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import { useIsDirty } from "@/shared/utils/forms/useIsDirty";
import { CardFileHorizontal } from "@easy/ui/cards";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    components: {
        PageSidebar,
        ButtonSecondaryIconSm,
        ArrowLeftIcon,
        FormInput,
        FormTextArea,
        FormToggleSm,
        CheckIcon,
        XIcon,
        FormFiles,
        CardFileHorizontal,
        FormLabel,
        cSaveButton,
        FormRichText,
    },
    props: {
        variant: {
            type: Object,
            required: true,
        },
        backupData: {
            type: Object,
            required: true,
        },
        mode: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            files: [],
            ecFile: {},
        };
    },
    computed: {
        ...mapState("pricing", ["pricing", "ecVariants"]),
        ecVariant() {
            if (this.pricing.ecId) {
                return this.ecVariants.find((x) => x.ecId === this.variant.ecId);
            }
            return null;
        },
    },
    watch: {
        variant: {
            handler(variant, oldVariant) {
                if (!oldVariant?.img && variant?.img) {
                    this.files = [
                        makeFileModel({
                            url: variant.img,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
        ecVariant: {
            handler(ecVariant, oldEcVariant) {
                if (!oldEcVariant?.img && ecVariant?.img) {
                    this.ecFile = makeFileModel({
                        url: ecVariant.img,
                    });
                }
            },
            immediate: true,
            deep: true,
        },
    },
    emits: ["cancel", "save"],
    setup(props) {
        useFocusOnQueryField();
        const { isDirty } = useIsDirty(props.backupData, props.variant);
        const { askToConfirmIfNeeded } = useSaveOrDropModal();
        return {
            isDirty,
            askToConfirmIfNeeded,
        };
    },
    methods: {
        ...mapActions("pricing", ["uploadVariantImg"]),
        removeImg() {
            this.variant.img = "";
        },
        uploadImg($event) {
            this.uploadVariantImg({
                variant: this.variant,
                file: $event,
            });
        },
        async quit() {
            const result = await this.askToConfirmIfNeeded({
                showModal: this.isDirty,
                onConfirmChanges: () => this.$emit("save"),
            });
            if (!result) {
                this.$emit("cancel");
            }
        },
    },
};
</script>
